import React, { memo, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormStep, FormStepProps, StepsState } from '../../../model/global';
import { currentStepSelector, formSummarySelector } from '../../../store/modules/steps/selectors';
import { getFormControlInitialState } from '../../utils';
import CustomFormField from '../../FormControls/CustomFormField';
import { CustomFormFieldState } from '../../FormControls/CustomFormField/CustomFormField';
import { stepsActions } from '../../../store/modules/steps/reducer';

const AdditionalInformation: React.FC<FormStepProps> = ({ data: { stepTemplate } }) => {

	const dispatch = useDispatch()
	const step = useSelector(currentStepSelector) as FormStep<'main'>
	const summary = useSelector(formSummarySelector) as StepsState['formSummary']
	const stepErrors = summary.validationErrors[step.stepTemplate];

	const fields: {
		errorCode: CustomFormFieldState | undefined,
		disorder: CustomFormFieldState | undefined,
		wolfService: CustomFormFieldState | undefined,
		appointmentWith: CustomFormFieldState | undefined,
		appointmentTime: CustomFormFieldState | undefined,
		errorDescription: CustomFormFieldState | undefined,
	} = useMemo(
		() => {
			return {
				errorCode: getFormControlInitialState('error_code', step.stepData.fields.main),
				disorder: getFormControlInitialState('disorder', step.stepData.fields.main),
				wolfService: getFormControlInitialState('wolf_service_checkbox', step.stepData.fields.main),
				appointmentWith: getFormControlInitialState('appointment_with', step.stepData.fields.main),
				appointmentTime: getFormControlInitialState('appointment_time', step.stepData.fields.main),
				errorDescription: getFormControlInitialState('error_description', step.stepData.fields.main),
			};
		},
		[step.stepData.fields.main]
	)

	// initialise the step data once
	useEffect(() => {
		// don't re-init fields
		if (summary.initialized[stepTemplate]) return;

		const initialData: Record<string, CustomFormFieldState> = {};

		// fill in state with initial field values and validity
		for (const [key, element] of Object.entries(fields)) {
			if (!element || !element.name) continue;

			const invalid = element.value === '' && element.required === true
			initialData[element.name] = {
				name: element.name as string,
				originalField: element.originalField,
				value: element.value,
				required: !!element.required,
				valid: !invalid,
				invalidReason: element.invalidReason || '',
			};
		}

		dispatch(stepsActions.initializeStepPayloadValues({
			stepTemplate,
			stepPayload: initialData,
			initialize: true,
		}));


	}, [dispatch, fields, stepTemplate, summary.initialized]);

	const fieldChangeHandler = (event: CustomFormFieldState): void => {
		dispatch(stepsActions.updateStepPayloadValues({
			stepTemplate,
			stepPayload: event,
		}));
	}

	if (!step) return null

	const allFields: CustomFormFieldState<any>[] = []
	step.stepData.fields.main.forEach(f => {
		if (!f.name) return;
		if (Object.keys(summary.steps[stepTemplate]).length === 0) return;
		const item = summary.steps[stepTemplate][f.name] ? summary.steps[stepTemplate][f.name] : undefined;
		item && allFields.push(item)
	})

	return (
		<div className='uk-grid-medium uk-grid uk-grid-stack user-none'>
			{allFields?.map((field, key) => {
				return <CustomFormField
					key={key}
					{...{ field, fieldErrors: stepErrors, onFieldChange: e => fieldChangeHandler(e) }}
				/>
			})}
		</div>
	);
};

export default memo(AdditionalInformation)
